import React from 'react';
import { Lock, TrendingUp, Users, Coins, Gift } from 'lucide-react';
import { colors, spacing, typography, borders } from '../../../../tokens';
import { SectionTitle } from '../../../../atoms';
import { formatTokenAmount } from '../utils/formatters';
import type { EnhancedContractStats } from '../hooks/useContractReads';

interface GlobalStatisticsProps {
  enhancedStats: EnhancedContractStats | undefined;
  activeStakers: bigint | undefined;
}

export const GlobalStatistics: React.FC<GlobalStatisticsProps> = ({
  enhancedStats,
  activeStakers,
}) => {
  // Calculate APY: (annual emission / total staked) * 100
  const currentAPY = enhancedStats?.contractTotalStaked && enhancedStats?.contractEmissionRate
    ? (() => {
        const secondsPerYear = 31536000; // 365 * 24 * 60 * 60
        const annualEmission = Number(enhancedStats.contractEmissionRate) * secondsPerYear;
        const totalStaked = Number(enhancedStats.contractTotalStaked);
        if (totalStaked === 0) return 0;
        return (annualEmission / totalStaked) * 100;
      })()
    : 0;

  return (
    <div>
      <style>{`
        @media (max-width: 768px) {
          .global-stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (min-width: 769px) and (max-width: 1200px) {
          .global-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
      <SectionTitle>Global statistics</SectionTitle>
      <div className="global-stats-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: spacing.lg,
      }}>
        {/* Total Staked */}
        <div style={{
          backgroundColor: colors.background.primary.default,
          padding: spacing.xl,
          borderRadius: borders.component.card.radius,
          border: `1px solid ${colors.border.default.default}`,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            marginBottom: spacing.sm,
          }}>
            <Lock size={24} color={colors.brand.purple.base} />
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              fontWeight: typography.fontWeight.medium,
            }}>
              Total Staked
            </span>
          </div>
          <div style={{
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.text.primary.default,
          }}>
            {enhancedStats?.contractTotalStaked ? formatTokenAmount(enhancedStats.contractTotalStaked) : '0.00'}
          </div>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.muted.default,
          }}>
            FAR tokens
          </div>
        </div>

        {/* Current APY */}
        <div style={{
          backgroundColor: colors.background.primary.default,
          padding: spacing.xl,
          borderRadius: borders.component.card.radius,
          border: `1px solid ${colors.border.default.default}`,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            marginBottom: spacing.sm,
          }}>
            <TrendingUp size={24} color={colors.semantic.success} />
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              fontWeight: typography.fontWeight.medium,
            }}>
              Current APY
            </span>
          </div>
          <div style={{
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.brand.cyan.base,
          }}>
            {currentAPY.toFixed(2)}%
          </div>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.muted.default,
          }}>
            Average with multiplier
          </div>
        </div>

        {/* Active Stakers */}
        <div style={{
          backgroundColor: colors.background.primary.default,
          padding: spacing.xl,
          borderRadius: borders.component.card.radius,
          border: `1px solid ${colors.border.default.default}`,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            marginBottom: spacing.sm,
          }}>
            <Users size={24} color={colors.brand.cyan.base} />
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              fontWeight: typography.fontWeight.medium,
            }}>
              Active Stakers
            </span>
          </div>
          <div style={{
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.text.primary.default,
          }}>
            {activeStakers !== undefined ? activeStakers.toString() : '0'}
          </div>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.muted.default,
          }}>
            Total participants
          </div>
        </div>

        {/* Emission Rate */}
        <div style={{
          backgroundColor: colors.background.primary.default,
          padding: spacing.xl,
          borderRadius: borders.component.card.radius,
          border: `1px solid ${colors.border.default.default}`,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            marginBottom: spacing.sm,
          }}>
            <Coins size={24} color={colors.semantic.success} />
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              fontWeight: typography.fontWeight.medium,
            }}>
              Emission Rate
            </span>
          </div>
          <div style={{
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.text.primary.default,
          }}>
            {enhancedStats?.contractEmissionRate ? formatTokenAmount(enhancedStats.contractEmissionRate) : '0.00'}
          </div>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.muted.default,
          }}>
            FAR per second
          </div>
        </div>

        {/* Total Claimed Rewards */}
        <div style={{
          backgroundColor: colors.background.primary.default,
          padding: spacing.xl,
          borderRadius: borders.component.card.radius,
          border: `1px solid ${colors.border.default.default}`,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            marginBottom: spacing.sm,
          }}>
            <Gift size={24} color={colors.semantic.success} />
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              fontWeight: typography.fontWeight.medium,
            }}>
              Total Claimed Rewards
            </span>
          </div>
          <div style={{
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.text.primary.default,
          }}>
            {enhancedStats?.contractTotalClaimed ? formatTokenAmount(enhancedStats.contractTotalClaimed) : '0.00'}
          </div>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.muted.default,
          }}>
            FAR tokens claimed globally
          </div>
        </div>
      </div>
    </div>
  );
};
